import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { formatCZK, formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { MapView } from "@/components/MapView";
import { HlavniDokumenty } from "@/components/docs/HlavniDokumenty";

const dayDiff = (iso) => {
  const d = new Date((iso || "").slice(0, 10) + "T00:00:00");
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / 86400000);
};

function Card({ title, action, children, testid }) {
  return (
    <div className="bg-card border border-border rounded-[14px] p-4" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="text-[15px] font-bold">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}
// Tolerantní klíč kategorie (165) — shodný se services.canon_category / Settings.
const canonCat = (s) => (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/\s+/g, " ").trim();

const Row = ({ label, value, strong, valueClass = "", title }) => (
  <div className="flex items-center justify-between gap-3 py-2 border-b border-border/60 last:border-0 text-sm">
    <span className={strong ? "font-bold" : "text-muted-foreground"} title={title}>{label}</span>
    <span className={`${strong ? "font-extrabold" : "font-bold"} tabular-nums text-right ${valueClass}`}>{value}</span>
  </div>
);
const TabLink = ({ onClick, children }) => (
  <button onClick={onClick} className="text-[13px] font-bold text-brand hover:underline">{children}</button>
);

export function ProjectInfoTab({ proj, eco, phases, manager, tasks, canEdit, onEdit, onGoTab, onSaved }) {
  const nav = useNavigate();
  const { company } = useAuth();
  const vatWord = company?.vat_payer === false ? "s DPH" : "bez DPH";
  const [diary, setDiary] = useState([]);
  useEffect(() => {
    if (manager) api.get(`/projects/${proj.id}/diary/overview`).then((r) => setDiary((r.data || []).slice(0, 2))).catch(() => {});
  }, [proj.id, manager]);

  const planned = eco ? (eco.total_costs || 0) + (eco.remaining_budget || 0) : proj.planned_budget || 0;
  const cat = eco?.by_category || {};
  // Skupinové součty (165): tolerantně dle kanonu, názvy se nikdy nepřepisují.
  const laborSum = cat["Práce"] || 0;
  const groupSum = (pred) => Object.entries(cat).reduce((s, [n, v]) => (n !== "Práce" && pred(canonCat(n)) ? s + (v || 0) : s), 0);
  const materialSum = groupSum((k) => k.startsWith("mat"));
  const subSum = groupSum((k) => k.startsWith("sub"));
  const otherSum = groupSum((k) => !k.startsWith("mat") && !k.startsWith("sub"));
  const upcoming = [...(phases || [])]
    .filter((p) => (p.progress || 0) < 100)
    .map((p) => ({ ...p, overdueDays: dayDiff(p.end_date) < 0 ? -dayDiff(p.end_date) : 0 }))
    .sort((a, b) => (b.overdueDays - a.overdueDays) || String(a.start_date).localeCompare(String(b.start_date)))
    .slice(0, 4);
  const DONE = ["Hotovo", "Schváleno"];
  const myTasks = (tasks || [])
    .filter((t) => !DONE.includes(t.status))
    .sort((a, b) => String(a.deadline || "9999").localeCompare(String(b.deadline || "9999")))
    .slice(0, 5);
  const missing = (v) => !v && manager
    ? <button onClick={onEdit} className="rounded-full bg-brand/10 border border-brand/25 text-brand px-2.5 py-0.5 text-xs font-bold">+ Doplnit</button>
    : (v || "—");

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4 items-start" data-testid="project-info-tab">
      <div className="space-y-4 min-w-0">
        {manager && eco && (
          <Card title="Peníze" action={<TabLink onClick={() => onGoTab("eco")}>Celá ekonomika</TabLink>} testid="info-money">
            <Row label="Cena díla" value={<>{formatCZK(proj.contract_price)} <span className="font-normal text-muted-foreground text-xs">{vatWord}</span></>} />
            <Row label="Rozpočet" value={<>{formatCZK(planned)} <span className="font-normal text-muted-foreground text-xs">{vatWord}</span></>} />
            <Row label="Náklady na lidi" value={formatCZK(laborSum)} />
            <Row label="Materiál" value={formatCZK(materialSum)} />
            <Row label="Subdodávky" title="Subdodávky (Sub - elektro, Sub - ZTI, …)" value={formatCZK(subSum)} />
            {otherSum > 0 && <Row label="Ostatní kategorie" value={formatCZK(otherSum)} />}
            <Row label="Zbývá z rozpočtu" strong value={formatCZK(eco.remaining_budget)}
              valueClass={eco.remaining_budget < 0 ? "text-destructive" : "text-success"} />
          </Card>
        )}
        {manager ? (
        <Card title="Harmonogram" testid="info-schedule"
          action={<div className="flex items-center gap-2">
            {upcoming.some((p) => p.overdueDays > 0) && (
              <span className="rounded-full bg-destructive/10 text-destructive px-2.5 py-0.5 text-xs font-bold">
                {upcoming.filter((p) => p.overdueDays > 0).length} po termínu
              </span>
            )}
            <TabLink onClick={() => onGoTab("schedule")}>Otevřít Gantt</TabLink>
          </div>}>
          {upcoming.length ? (
            <div className="space-y-2">
              {upcoming.map((p) => (
                <div key={p.id} className={`flex items-center justify-between gap-3 rounded-[12px] border px-3.5 py-3 ${p.overdueDays > 0 ? "border-destructive/40 bg-destructive/5" : "border-border"}`}>
                  <span className="text-sm font-semibold truncate">{p.name}</span>
                  {p.overdueDays > 0
                    ? <span className="text-xs font-bold text-destructive whitespace-nowrap">{p.overdueDays} {p.overdueDays === 1 ? "den" : p.overdueDays < 5 ? "dny" : "dní"} po termínu</span>
                    : <span className="text-[13px] text-muted-foreground whitespace-nowrap tabular-nums">{formatDate(p.end_date)}</span>}
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">Žádné nadcházející události.</p>}
        </Card>
        ) : (
        <Card title="Moje úkoly na téhle zakázce" testid="info-my-tasks"
          action={<TabLink onClick={() => nav("/ukoly")}>Všechny úkoly</TabLink>}>
          {myTasks.length ? (
            <div className="space-y-2">
              {myTasks.map((t) => {
                const late = t.deadline && dayDiff(t.deadline) < 0;
                return (
                  <div key={t.id} data-testid={`info-task-${t.id}`}
                    className={`flex items-center justify-between gap-3 rounded-[12px] border px-3.5 py-3 ${late ? "border-destructive/40 bg-destructive/5" : "border-border"}`}>
                    <span className="text-sm font-semibold truncate">{t.title}</span>
                    {t.deadline
                      ? <span className={`text-xs font-bold whitespace-nowrap tabular-nums ${late ? "text-destructive" : "text-muted-foreground"}`}>
                          {late ? `${-dayDiff(t.deadline)} dní po termínu` : formatDate(t.deadline)}
                        </span>
                      : <span className="text-[13px] text-muted-foreground whitespace-nowrap">bez termínu</span>}
                  </div>
                );
              })}
            </div>
          ) : <p className="text-sm text-muted-foreground">Na téhle zakázce pro tebe teď žádný úkol není.</p>}
        </Card>
        )}
        {manager && (
          <Card title="Poslední z deníku" action={<TabLink onClick={() => onGoTab("diary")}>Celý deník</TabLink>} testid="info-diary">
            {diary.length ? diary.map((d, i) => (
              <div key={d.date} className={`grid grid-cols-[72px_1fr] gap-3 ${i > 0 ? "border-t border-border/60 pt-3 mt-3" : ""}`}>
                <span className="text-[13px] font-bold text-brand tabular-nums">{formatDate(d.date)}</span>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{d.work_performed || "Zápis do deníku"}</div>
                  <div className="text-xs text-muted-foreground">{d.author_name || "—"}{d.photo_count ? ` · ${d.photo_count} fotky` : ""}</div>
                </div>
              </div>
            )) : <p className="text-sm text-muted-foreground">Zatím žádný zápis.</p>}
          </Card>
        )}
      </div>

      <div className="space-y-4 min-w-0">
        <Card title="Údaje o zakázce" testid="info-facts">
          <Row label="Číslo" value={missing(proj.code)} />
          <Row label="Adresa" value={missing(proj.address)} />
          <Row label="Investor" value={missing(proj.investor?.name)} />
          {proj.area_m2 ? <Row label="Plocha" value={`${proj.area_m2} m²`} /> : null}
          {proj.description && <p className="text-sm text-muted-foreground pt-2">{proj.description}</p>}
        </Card>
        <HlavniDokumenty project={proj} manager={manager} canEdit={canEdit} onGoDocs={() => onGoTab("docs")} onSaved={onSaved} />
        {proj.lat != null && <MapView lat={proj.lat} lng={proj.lng} height={220} />}
      </div>
    </div>
  );
}
